/**
 * AWDD Theme — Main JavaScript
 * Uses GSAP + ScrollTrigger for animations
 */

import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import initHeader from './header.js';
import initHeroVideo from './hero-video.js';
import { initCarousels } from './carousel.js';
import { initLeadership } from './leadership.js';
import initVideoFacade from './video-facade.js';
import initGallery from './gallery.js';
import { initDocumentHub } from './document-hub.js';
import initLightbox from './lightbox.js';
import initProjects from './projects.js';
import { initProductGallery, initProductStockTabs } from './product.js';
import initForms from './forms.js';

gsap.registerPlugin(ScrollTrigger);

// Tell the head gate (awdd_reveal_gate() in functions.php) that the bundle booted,
// so it keeps '.awdd-anim' in place instead of un-hiding everything at its deadline.
window.awddRevealReady = true;

// Dismiss the page loader (inc/loader.php). This is the NORMAL path, not the
// only one — that script also dismisses on window 'load' and at a 3s deadline,
// because a curtain that only this line can lift would outlive a bundle that
// 404s and hide a fully rendered page for good. Called here rather than on
// DOMContentLoaded so a cached page clears it as early as possible; the
// minimum display is enforced on the loader's side.
window.awddDismissLoader?.();

document.addEventListener('DOMContentLoaded', function () {

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    initHeader();
    initHeroVideo();
    initCarousels();
    initLeadership();
    initVideoFacade();
    initGallery();
    initDocumentHub();
    initLightbox();
    initProjects();
    initProductGallery();
    initProductStockTabs();
    initForms();

    // ===== Blog year filter =====
    // The chips are plain links; only the year control needs JS, and its
    // <noscript> sibling renders the same years as links, so a dead bundle
    // leaves a working filter rather than an inert select.
    document.querySelectorAll('[data-awdd-blog-year]').forEach((select) => {
        select.addEventListener('change', () => {
            if (select.value) window.location.href = select.value;
        });
    });

    // ===== Scroll reveals =====
    // .reveal starts `visibility: hidden` in CSS; GSAP's autoAlpha tween drives
    // opacity + visibility together via inline styles, then y settles it into
    // place. Under reduced motion we skip the tween entirely and just show
    // the element in its resting position (no rise, no fade).
    document.querySelectorAll('.reveal').forEach(el => {
        if (prefersReducedMotion) {
            gsap.set(el, { autoAlpha: 1, y: 0 });
            return;
        }

        const delayMatch = el.className.match(/reveal-delay-(\d+)/);
        const delay = delayMatch ? parseInt(delayMatch[1]) * 0.15 : 0;

        gsap.fromTo(el,
            { autoAlpha: 0, y: 28 },
            {
                autoAlpha: 1, y: 0, duration: 0.8, ease: 'power2.out',
                delay,
                scrollTrigger: { trigger: el, start: 'top 90%', toggleActions: 'play none none none' }
            }
        );
    });

    // Recalculate ScrollTrigger positions after webfonts and images settle,
    // otherwise reveals below the fold can stay hidden when initial layout
    // shifts (e.g. font swap, lazy-loaded images).
    window.addEventListener('load', () => ScrollTrigger.refresh());
    if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(() => ScrollTrigger.refresh());
    }
    // Safety net: if any .reveal element is still hidden 2s after load (because
    // ScrollTrigger never fired for it), force-show it. Matches the "always
    // visible" intent (the fade is a nicety, not a gate).
    window.addEventListener('load', () => {
        setTimeout(() => {
            document.querySelectorAll('.reveal').forEach(el => {
                const cs = getComputedStyle(el);
                if (cs.visibility === 'hidden') {
                    gsap.set(el, { autoAlpha: 1, y: 0 });
                }
            });
        }, 2000);
    });

    // ===== Accordions =====
    // [data-awdd-accordion] wraps a toggle button and a [data-awdd-accordion-panel].
    // No current consumer: drawer.php (the mobile drawer) deliberately renders
    // its Products children as a plain indented sub-list rather than an
    // accordion. Kept as a generic helper for the next place that needs a
    // collapsible group — wire up the [data-awdd-accordion] markup and this
    // block runs unchanged. Panels animate via max-height + opacity rather
    // than display:none so the transition can run.
    document.querySelectorAll('[data-awdd-accordion]').forEach((accordion, accIndex) => {
        const toggle = accordion.querySelector('button');
        const panel = accordion.querySelector('[data-awdd-accordion-panel]');
        if (!toggle || !panel) return;

        // aria-expanded on its own says "something expands" without saying
        // what. The panel is markup-only, so the id is minted here.
        if (!panel.id) panel.id = `awdd-accordion-panel-${accIndex + 1}`;
        toggle.setAttribute('aria-controls', panel.id);

        panel.style.overflow = 'hidden';
        panel.style.maxHeight = '0px';
        panel.style.opacity = '0';
        if (!prefersReducedMotion) {
            panel.style.transition = 'max-height 0.4s ease, opacity 0.3s ease';
        }
        toggle.setAttribute('aria-expanded', 'false');
        // max-height: 0 clips the panel but leaves its links in the tab order,
        // so tabbing into a collapsed group silently scrolled the clipped box.
        panel.inert = true;

        toggle.addEventListener('click', () => {
            const isOpen = toggle.getAttribute('aria-expanded') === 'true';
            const nextOpen = !isOpen;

            toggle.setAttribute('aria-expanded', String(nextOpen));
            accordion.classList.toggle('is-open', nextOpen);
            panel.style.maxHeight = nextOpen ? panel.scrollHeight + 'px' : '0px';
            panel.style.opacity = nextOpen ? '1' : '0';
            panel.inert = !nextOpen;
        });

        // Keep an open panel's max-height correct if content reflows
        // (viewport resize, webfont swap).
        window.addEventListener('resize', () => {
            if (toggle.getAttribute('aria-expanded') === 'true') {
                panel.style.maxHeight = panel.scrollHeight + 'px';
            }
        }, { passive: true });
    });

    // ===== Smooth scroll =====
    // scrollIntoView({block:'start'}) puts the target's top at viewport 0,
    // which is BEHIND the position:fixed header — the target's own heading is
    // the part that disappears. Offset by the bar's measured height instead.
    // Measured at click time, not cached: the bar collapses its utility row
    // when scrolled, so its height differs between the at-rest and scrolled
    // states and a value read at load would be wrong by ~33px.
    document.querySelectorAll('a[href^="#"]').forEach(a => {
        a.addEventListener('click', function (e) {
            const href = this.getAttribute('href');
            if (href === '#') return;

            const t = document.querySelector(href);
            if (!t) return;

            e.preventDefault();

            const bar = document.querySelector('[data-awdd-header]');
            // getBoundingClientRect().height, not offsetHeight: above 1900px the
            // reference's ultra-wide `zoom` tiers (not ported here, but the target
            // rect below is still measured live in whatever coordinate space the
            // browser is in) scale getBoundingClientRect() but not offsetHeight,
            // so mixing the two under-scaled the bar measurement and landed the
            // target partly behind it. Keep both measurements in the same space.
            const offset = (bar ? bar.getBoundingClientRect().height : 0) + 16;

            window.scrollTo({
                top: Math.max(0, t.getBoundingClientRect().top + window.scrollY - offset),
                behavior: 'smooth'
            });
        });
    });

    /* ── Copy article link ──
       The email and LinkedIn share controls are plain links and need no JS.
       This is the only one that does. navigator.clipboard is unavailable on
       insecure origins, so fall back to a hidden textarea + execCommand rather
       than leaving the button doing nothing. */
    document.querySelectorAll('[data-awdd-copy]').forEach(btn => {
        const status = btn.parentElement && btn.parentElement.querySelector('.awdd-copy-status');

        const announce = (msg) => {
            if (!status) return;
            status.textContent = msg;
            clearTimeout(status._awddTimer);
            status._awddTimer = setTimeout(() => { status.textContent = ''; }, 2500);
        };

        const legacyCopy = (text) => {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.cssText = 'position:fixed;top:0;left:-9999px;opacity:0;';
            document.body.appendChild(ta);
            ta.select();
            let ok = false;
            try { ok = document.execCommand('copy'); } catch (e) { ok = false; }
            ta.remove();
            return ok;
        };

        btn.addEventListener('click', async () => {
            const url = btn.getAttribute('data-awdd-copy');
            if (!url) return;

            let copied = false;
            if (navigator.clipboard && window.isSecureContext) {
                try {
                    await navigator.clipboard.writeText(url);
                    copied = true;
                } catch (e) {
                    copied = false;
                }
            }
            if (!copied) copied = legacyCopy(url);

            announce(copied ? 'Link copied' : 'Press Ctrl+C to copy');
            btn.classList.toggle('is-copied', copied);
            setTimeout(() => btn.classList.remove('is-copied'), 2500);
        });
    });

    /* ── Scroll to top button ── */
    const scrollTopBtn = document.getElementById('scrollTopBtn');
    if (scrollTopBtn) {
        window.addEventListener('scroll', () => {
            scrollTopBtn.classList.toggle('visible', window.scrollY > 400);
        }, { passive: true });
        scrollTopBtn.addEventListener('click', () => {
            window.scrollTo({ top: 0, behavior: 'smooth' });
        });
    }

});
